import { createApiErrorHandler, useApiClient } from '@m43/core';
import { useMutation } from '@tanstack/react-query';
import { ButtonGroup, ModalBody, ModalFooter, ModalHeader, Stack, useToast } from '@cad-arc';
import { CancelRotationCommand, RotationResource } from '@mark43/cad-api';
import { useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { errorOccurredWhile } from '../common/helpers/error-occurred-while';
import { FormSaveButton } from '../forms/integrated-form-components/form-save-button';
import { FormSelect } from '../forms/integrated-form-controls/form-select';
import { FormTextarea } from '../forms/integrated-form-controls/form-text-area';
import { IntegratedFormProvider } from '../forms/integrated-form-provider';
import { useEnabledFeatures } from '~/common/hooks/enabled-features';
import { FormCheckbox } from '~/forms/integrated-form-controls/form-checkbox';
import { globalAttributes, serviceRotationActionTypeToDisplay } from '~/common/global-attributes';

const dispositionOptions = [
    {
        label: 'Provider Cancellation',
        value: 'true',
    },
    {
        label: 'User Cancellation',
        value: 'false',
    },
];

type CancelServiceRotationModalForm = Omit<
    CancelRotationCommand,
    'isProviderCancel' | 'overrideActionAttributeId'
> & {
    isProviderCancel: string;
    isActionOverride: boolean;
    overrideActionAttributeId?: number;
};

const requiredRule = {
    required: 'Required',
};

const actionOverrideOptions = [
    globalAttributes.serviceRotationActionType.ROTATE,
    globalAttributes.serviceRotationActionType.REVERT,
    globalAttributes.serviceRotationActionType.NO_ACTION,
].map((value) => ({
    label: serviceRotationActionTypeToDisplay[value],
    value,
}));

export function CancelServiceRotationModal({
    id,
    onClose,
    serviceTypeName,
}: {
    id: number;
    onClose: () => void;
    serviceTypeName: string;
}): React.ReactElement {
    const form = useForm<CancelServiceRotationModalForm>({
        defaultValues: {
            id,
            isActionOverride: false,
        },
    });
    const apiClient = useApiClient();
    const toast = useToast();

    const enabledFeatures = useEnabledFeatures();
    const isActionOverride = useWatch({
        control: form.control,
        name: 'isActionOverride',
    });

    useEffect(() => {
        if (!isActionOverride) {
            form.setValue('overrideActionAttributeId', undefined);
        }
    }, [form, isActionOverride]);

    const cancelServiceRotation = useMutation(
        (params: CancelServiceRotationModalForm) => {
            return apiClient.request<RotationResource.CancelRotationListPosition>({
                method: 'POST',
                url: 'rotation/cancel_action',
                data: {
                    id: params.id,
                    reason: params.reason,
                    isProviderCancel: params.isProviderCancel === 'true',
                    overrideActionAttributeId: params.isActionOverride
                        ? params.overrideActionAttributeId
                        : undefined,
                },
            });
        },
        {
            onSuccess: onClose,
            onError: createApiErrorHandler(() => {
                toast({
                    status: 'error',
                    description: errorOccurredWhile('cancelling the service rotation request'),
                });
            }),
        }
    );

    return (
        <IntegratedFormProvider mutation={cancelServiceRotation} form={form}>
            <>
                <ModalHeader>Cancel {serviceTypeName} Service Request</ModalHeader>
                <ModalBody>
                    <Stack>
                        <FormSelect
                            autoFocus={true}
                            name="isProviderCancel"
                            label="Disposition"
                            control={form.control}
                            rules={requiredRule}
                            disableSetFullOptionAsValue={true}
                            selectProps={{ isDefaultOpen: true }}
                            options={dispositionOptions}
                        />
                        {enabledFeatures.enhancedServiceRotation ? (
                            <>
                                <FormCheckbox
                                    name="isActionOverride"
                                    label="Override Action"
                                    control={form.control}
                                />
                                {isActionOverride ? (
                                    <FormSelect
                                        name="overrideActionAttributeId"
                                        label="Action"
                                        control={form.control}
                                        rules={requiredRule}
                                        disableSetFullOptionAsValue={true}
                                        options={actionOverrideOptions}
                                    />
                                ) : null}
                            </>
                        ) : null}
                        <FormTextarea
                            control={form.control}
                            name="reason"
                            label="Reason"
                            rules={requiredRule}
                        />
                    </Stack>
                </ModalBody>
                <ModalFooter>
                    <ButtonGroup>
                        <FormSaveButton>OK</FormSaveButton>
                    </ButtonGroup>
                </ModalFooter>
            </>
        </IntegratedFormProvider>
    );
}
